import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { IconButton, InputAdornment, Tooltip } from '@mui/material';
import { SearchContainer, StyledTextField } from './ExpandableSearch.styles';

export interface ExpandableSearchHandle {
  expand: () => void;
}

interface ExpandableSearchProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}

export const ExpandableSearch = forwardRef<ExpandableSearchHandle, ExpandableSearchProps>(({
  value,
  onChange,
  placeholder = 'Search...',
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleExpand = () => {
    setIsExpanded(true);
    // Focus input after expansion animation starts
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  useImperativeHandle(ref, () => ({
    expand: handleExpand,
  }));

  const handleClear = () => {
    // Create synthetic event for onChange
    const syntheticEvent = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    inputRef.current?.focus();
  };

  const handleBlur = () => {
    // Only collapse if there's no text
    if (!value) {
      setIsExpanded(false);
    }
  };

  return (
    <SearchContainer isExpanded={isExpanded}>
      {isExpanded ? (
        <StyledTextField
          inputRef={inputRef}
          size="small"
          variant="outlined"
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'action.active' }} />
                </InputAdornment>
              ),
              endAdornment: value ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      ) : (
        <Tooltip title="Search by company name" arrow>
          <IconButton onClick={handleExpand} size="small">
            <SearchIcon />
          </IconButton>
        </Tooltip>
      )}
    </SearchContainer>
  );
});
